
import { Activity, MoveActivityMode } from 'farmdok-rest-api';
import { defineComponent } from 'vue';
import { mapGetters, mapState } from 'vuex';

import { Getters as MoveActivityGetters } from '@/activities/modals/moveActivity/store/getters';
import getFieldDisplayName from '@/activities/utils/getFieldDisplayName';
import { fetchFieldsByProcessOrderIds } from '@/shared/api/rest/RestApi';
import { Field } from '@/shared/api/rest/models';
import { DropdownItem } from '@/shared/components/form/formFieldDropdownTypes';
import { DropdownItems } from '@/shared/handsontable/types';

import ConflictionStep from '../components/ConflictionStep.vue';
import { MoveActivityParameterWithResult } from '../store/types';

export default defineComponent({
  name: 'ConflictionStepContainer',
  components: {
    ConflictionStep,
  },
  data() {
    return {
      destinationFields: [] as Field[],
    };
  },
  async created() {
    this.$store.dispatch('activityTypes/subscribe');
    this.$store.dispatch('fields/subscribe');

    if (this.selectedProcessOrderId) {
      const { data } = await fetchFieldsByProcessOrderIds([this.selectedProcessOrderId], undefined, {
        resolve: ['fieldGroup'],
        itemsPerPage: 5000,
      });
      if (data.status === 'success') {
        this.destinationFields = data.data;
      }
    }
  },
  computed: {
    ...mapGetters({
      activities: 'activities/data',
      fields: 'fields/data',
      activityTypes: 'activityTypes/data',
      customers: 'customers/data',
      crops: 'products/crops/data',
    }),
    ...mapState('activities/moveActivity', [
      'counterCurrentMoveActivityConflict',
      'moveActivityParametersWithResult',
      'selectedProcessOrderId',
      'keepOption',
    ]),
    fieldDropdownItems(): DropdownItems[] {
      return [
        {
          name: this.$t('Felder') as string,
          id: 'fields',
          items: this.destinationFields.map((field) => ({
            id: field.id,
            name: getFieldDisplayName(field, this.customers, this.crops),
          })),
          sort: true,
        },
      ];
    },
    moveActivityParametersWithConflict(): MoveActivityParameterWithResult[] | null {
      return this.$store.getters[
        'activities/moveActivity/moveActivityParametersWithConflict'
      ] as MoveActivityGetters['moveActivityParametersWithConflict'];
    },
    moveActivityParametersWithoutMode(): MoveActivityParameterWithResult[] | null {
      return this.$store.getters[
        'activities/moveActivity/moveActivityParametersWithoutMode'
      ] as MoveActivityGetters['moveActivityParametersWithoutMode'];
    },
    currentMoveActivityParameter(): MoveActivityParameterWithResult | null {
      if (this.moveActivityParametersWithConflict) {
        return this.moveActivityParametersWithConflict[this.counterCurrentMoveActivityConflict];
      }
      return null;
    },
    selectedField(): DropdownItem | null {
      if (this.currentMoveActivityParameter) {
        return (
          this.fieldDropdownItems[0].items.find(
            (field) => field.id === this.currentMoveActivityParameter?.destinationFieldId,
          ) || null
        );
      }
      return null;
    },
    selectedMode(): MoveActivityMode | null {
      return this.currentMoveActivityParameter?.mode || null;
    },
    activity(): Activity | null {
      if (this.currentMoveActivityParameter) {
        return this.activities[this.currentMoveActivityParameter.sourceActivityId];
      }
      return null;
    },
  },
  methods: {
    updateFieldId(fieldId: string | null): void {
      this.$store.commit('activities/moveActivity/setDestinationFieldIdOnMoveActivityParametersWithResult', {
        id: this.currentMoveActivityParameter?.sourceActivityId,
        destinationFieldId: fieldId,
      });
    },
    updateMode(mode: MoveActivityMode, sourceActivityId?: string): void {
      this.$store.commit('activities/moveActivity/setModeOnMoveActivityParametersWithResult', {
        id: sourceActivityId || this.currentMoveActivityParameter?.sourceActivityId,
        mode,
      });
    },
    updateKeepOption(keepOption: boolean): void {
      this.$store.commit(`activities/moveActivity/setKeepOption`, keepOption);

      if (keepOption) {
        this.moveActivityParametersWithoutMode?.forEach((moveActivity) => {
          this.updateMode(MoveActivityMode.CopyField, moveActivity.sourceActivityId);
        });
      }
    },
  },
});
